import { BooksStatus, Payee, PayrollType, Trips } from '@prisma/client';
import * as yup from 'yup';
import { LoadedUser } from '../../interfaces';
import { OnSchedCommonType, TypeOnSchedResponse } from '../on-sched';
import { testPhoneNumbervalue } from '../../utils/global/helper';
import { getEmailSchema } from '../global';

export type ShortDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export enum EmployeeAvailabilityError {
  FewAvailableDays = 'FewAvailableDays',
  InvalidDuration = 'InvalidDuration',
}

export type Repeat = {
  frequency: string;
  interval: number;
  weekdays?: string;
  monthDay?: number;
  monthyType?: string;
};

export type ResourceBlock = {
  object?: string;
  id?: number;
  businessId?: number;
  resourceId?: number;
  startDate: string;
  endDate: string;
  startTime: number;
  endTime: number;
  reason: string;
  repeats: boolean;
  repeat?: Repeat;
};

type BaseParams = {
  employeeId: string;
  employees: LoadedUser['business']['employees'];
  currentUser: LoadedUser;
};

export type EmployeeAvailabilityErrorParams = BaseParams &
  (
    | {
        code: EmployeeAvailabilityError.FewAvailableDays;
        durationMins: number;
        days: ShortDay[];
      }
    | {
        code: EmployeeAvailabilityError.InvalidDuration;
        durationMins: number;
        maximumDurationMins: number;
      }
  );

type BaseResponse = {
  errorTitle: string;
  errorMessage: string;
  type: 'error' | 'warning';
};

export type EmployeeAvailablityErrorResponse = BaseResponse &
  (
    | { code: EmployeeAvailabilityError.FewAvailableDays }
    | {
        code: EmployeeAvailabilityError.InvalidDuration;
        maximumDuration: number;
      }
  );

export type GetBatchTimeBlocksResponse = Record<string, ResourceBlock[]>;

export type TripWithTimes = Trips & {
  weeklyHours?: OnSchedCommonType.Availability;
};

export type GetBatchTripsResponse = Record<string, TripWithTimes[]>;

export type ResourceWithDBTimezone = TypeOnSchedResponse.Resource & {
  userDbTimezone: string | null;
};

export type EmployeesResourceMap = Record<string, ResourceWithDBTimezone>;

export enum EmployeesRole {
  SHOP_MANAGER = 'SHOP_MANAGER',
  SHOP_BOOKER = 'SHOP_BOOKER',
  ARTIST = 'ARTIST',
  PIERCER = 'PIERCER',
}

export const selectedRolesSchema = yup
  .array(yup.mixed<EmployeesRole>().oneOf(Object.values(EmployeesRole)))
  .min(1, 'Please select at least one role')
  .required('Please select at least one role');

export const updateEmployeeSchema = yup.object({
  employeeToBusiness: yup
    .object({
      businessId: yup.string().required('Invalid shop'),
      payrollType: yup
        .mixed<PayrollType>()
        .oneOf(Object.values(PayrollType), 'Invalid payroll type')
        .optional(),
      commissionPercent: yup
        .number()
        .max(1, 'Invalid commission percent')
        .min(0, 'Invalid commission percent')
        .optional(),
      isBookable: yup.boolean().optional(),
      addToAllChats: yup.boolean().optional(),
      generalRequestOwner: yup.boolean().optional(),
      canViewReports: yup.boolean().optional(),
      canManageWaivers: yup.boolean().optional(),
      canIssueRefunds: yup.boolean().optional(),
      canViewAllShopCustomers: yup.boolean().optional(),
      canManageTeam: yup.boolean().optional(),
      canManageCampaigns: yup.boolean().optional(),
      payeeForOtherPayroll: yup
        .mixed<Payee>()
        .oneOf(Object.values(Payee), 'Invalid payee')
        .optional(),
      color: yup.string().nullable().optional(),
      defaultHourlyRate: yup.number().min(0).nullable().optional(),
    })
    .nullable()
    .optional()
    .default(null),
  email: getEmailSchema('Invalid email').required('Email is required'),
  phoneNumber: yup
    .string()
    .test({
      test: testPhoneNumbervalue,
      message: 'Invalid phone number',
    })
    .required('Phone number is required'),
  firstName: yup
    .string()
    .test({
      test: str => !!str?.trim(),
      message: 'First name is required',
    })
    .required(),
  lastName: yup
    .string()
    .test({
      test: str => !!str?.trim(),
      message: 'Last name is required',
    })
    .required(),
  displayName: yup
    .string()
    .test({
      test: str => !!str?.trim(),
      message: 'Display name is required',
    })
    .required(),
  about: yup.string().optional(),
  profileImage: yup.string().optional(),
  standardDesignMessage: yup.string().optional(),
  cancellationPolicyText: yup.string().optional(),
  liabilityWaiver: yup.string().optional(),
  minCancellationDays: yup.number().optional(),
  maxCancellationTimes: yup.number().optional(),
  bookAheadLimitDays: yup.number().optional(),
  bookInAdvanceMinutes: yup.number().optional(),
  // urlPath: yup
  //   .string()
  //   .test({
  //     test: str =>
  //       !!str?.trim() && /^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/.test(str),
  //     message: 'Invalid booking link',
  //   })
  //   .optional(),
  minAppointmentPrice: yup.number().optional(),
  timezone: yup.string().optional(),
  selectedRoles: selectedRolesSchema,
  stackDays: yup.boolean().optional(),
  stackTimes: yup.boolean().optional(),
  booksStatus: yup
    .mixed<BooksStatus>()
    .oneOf(Object.values(BooksStatus), 'Invalid books status')
    .optional(),
});

export type UpdateEmployeePayload = yup.InferType<typeof updateEmployeeSchema>;

export interface UpdateEmployeeResourcePayload {
  availability: OnSchedCommonType.Availability;
}
